import React, { useState } from 'react';
import { useAccessibility } from '@/context/AccessibilityContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Accessibility } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const AccessibilityButton = () => {
  const { settings, updateFontSize, toggleHighContrast } = useAccessibility();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const fontSizeOptions = [
    { value: 'normal' as const, label: t('font_size_normal') || 'Normal' },
    { value: 'large' as const, label: t('font_size_large') || 'Large' },
    { value: 'extra-large' as const, label: t('font_size_extra_large') || 'Extra Large' },
  ];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex items-center p-2"
          aria-label={t('accessibility_menu') || 'Accessibility options'}
        >
          <Accessibility className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-white dark:bg-gray-800 border shadow-lg p-4 z-50"
        side="bottom"
      >
        <div className="space-y-4">
          {/* Font Size Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Accessibility className="h-3 w-3" />
              {t('font_size') || 'Font Size'}
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {fontSizeOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={settings.fontSize === option.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateFontSize(option.value)}
                  className="text-xs"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* High Contrast Section */}
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              {t('high_contrast') || 'High Contrast'}
            </Label>
            <Switch
              checked={settings.highContrast}
              onCheckedChange={toggleHighContrast}
              aria-label={t('toggle_high_contrast') || 'Toggle high contrast mode'}
            />
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccessibilityButton;